// src/app/services/api/api.service.ts
import { Injectable } from "@angular/core";
import {
  addDoc,
  collection,
  collectionData,
  deleteDoc,
  doc,
  docData,
  Firestore,
  getDoc,
  getDocs,
  limit,
  orderBy,
  OrderByDirection,
  query,
  QueryConstraint,
  setDoc,
  startAfter,
  updateDoc,
  where,
} from "@angular/fire/firestore";

import { FirebaseApp } from "@angular/fire/app";

import {
  CollectionReference,
  DocumentData,
  DocumentReference,
  Firestore as FirebaseFirestore,
  GeoPoint,
  collection as fbCollection,
  doc as fbDoc,
  getFirestore as fbGetFirestore,
} from "firebase/firestore";

import {
  getDownloadURL,
  ref,
  Storage,
  uploadBytes,
} from "@angular/fire/storage";

import * as geofirestore from "geofirestore";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";

import { environment } from "../../../environments/environment";
import { Observable } from "rxjs";

type MaybeConstraints = QueryConstraint | QueryConstraint[] | undefined;
type WithId<T> = T & { id: string };

@Injectable({ providedIn: "root" })
export class ApiService {
  // modular Firestore instance
  private readonly db: FirebaseFirestore;

  // compat Firestore for geofirestore
  private readonly compatFirestore: firebase.firestore.Firestore;

  // geofirestore wrapper
  readonly GeoFirestore: any;

  constructor(
    private readonly app: FirebaseApp,
    public readonly firestore: Firestore,
    private readonly storage: Storage
  ) {
    // Modular (always) - same injected FirebaseApp
    this.db = fbGetFirestore(this.app);

    // ✅ Compat (ONLY for GeoFirestore) - reuse same app context (do NOT init a second app)
    let compatApp: firebase.app.App;
    try {
      compatApp = firebase.app(this.app.name);
    } catch {
      compatApp = firebase.initializeApp(
        environment.firebase as any,
        this.app.name
      );
    }

    this.compatFirestore = compatApp.firestore();
    this.GeoFirestore = geofirestore.initializeApp(this.compatFirestore);
  }

  // --------- Geo helpers ---------
  getGeoPoint(lat: number, lng: number) {
    return new GeoPoint(lat, lng);
  }

  geoCollection(path: string) {
    return this.GeoFirestore.collection(path);
  }

  // --------- Modular refs (used across app) ---------
  collectionRef(path: string): CollectionReference<DocumentData> {
    return fbCollection(this.db, path);
  }

  document(path: string): DocumentReference<DocumentData> {
    return fbDoc(this.db, path);
  }

  // --------- AngularFire helpers ---------
  // Overloads to make idField typing happy
  collectionDataQuery<T extends object = any>(
    path: string,
    queryFn?: MaybeConstraints,
    withId?: false
  ): Observable<T[]>;
  collectionDataQuery<T extends object = any>(
    path: string,
    queryFn: MaybeConstraints | undefined,
    withId: true
  ): Observable<WithId<T>[]>;
  collectionDataQuery<T extends object = any>(
    path: string,
    queryFn?: MaybeConstraints,
    withId: boolean = false
  ): Observable<any[]> {
    let dataRef: any = collection(this.firestore, path);

    if (queryFn) {
      const constraints = Array.isArray(queryFn) ? queryFn : [queryFn];
      dataRef = query(dataRef, ...constraints);
    }

    if (withId) {
      // ✅ Now 'id' is guaranteed to be a key of the generic type
      return collectionData<WithId<T>>(dataRef, { idField: "id" });
    }

    return collectionData<T>(dataRef);
  }

  docDataQuery<T extends object = any>(
    path: string,
    id: string,
    withId?: false
  ): Observable<T>;
  docDataQuery<T extends object = any>(
    path: string,
    id: string,
    withId: true
  ): Observable<WithId<T>>;
  docDataQuery<T extends object = any>(
    path: string,
    id: string,
    withId: boolean = true
  ): Observable<any> {
    const dataRef: any = doc(this.firestore, path, id);

    if (withId) {
      // ✅ Now 'id' is guaranteed
      return docData<WithId<T>>(dataRef, { idField: "id" });
    }

    return docData<T>(dataRef);
  }

  collectionFirestoreRef(path: string) {
    return collection(this.firestore, path);
  }

  getDocs(path: string, queryFn?: MaybeConstraints) {
    let dataRef: any = collection(this.firestore, path);

    if (queryFn) {
      const constraints = Array.isArray(queryFn) ? queryFn : [queryFn];
      dataRef = query(dataRef, ...constraints);
    }

    return getDocs(dataRef);
  }

  getDocsByQuery(q: any) {
    return getDocs(q);
  }

  // Query helpers
  whereQuery(fieldPath: string, condition: any, value: any) {
    return where(fieldPath as any, condition, value);
  }

  orderByQuery(fieldPath: string, directionStr: OrderByDirection = "asc") {
    return orderBy(fieldPath as any, directionStr);
  }

  limitQuery(number: number) {
    return limit(number);
  }

  startAfterQuery(data: any) {
    return startAfter(data);
  }

  // Document helpers
  getDocById(path: string) {
    const dataRef: any = doc(this.firestore, path);
    return getDoc(dataRef);
  }

  getDocByReference(dataRef: any) {
    return getDoc(dataRef);
  }

  addDocument(path: string, data: any) {
    const dataRef: any = collection(this.firestore, path);
    return addDoc(dataRef, data);
  }

  /**
   * ✅ Default merge=true (safer)
   */
  async setDocument(path: string, data: any, merge: boolean = true) {
    const dataRef: any = doc(this.firestore, path);
    if (merge) return setDoc(dataRef, data, { merge: true });
    return setDoc(dataRef, data);
  }

  updateDocument(path: string, data: any) {
    const dataRef: any = doc(this.firestore, path);
    return updateDoc(dataRef, data);
  }

  deleteDocument(path: string) {
    const dataRef: any = doc(this.firestore, path);
    return deleteDoc(dataRef);
  }

  async uploadImage(
    file: Blob | Uint8Array | ArrayBuffer,
    filePath: string
  ): Promise<string> {
    const fileRef = ref(this.storage, filePath);
    await uploadBytes(fileRef, file as any);
    return await getDownloadURL(fileRef);
  }
}
